/* eslint-disable no-unused-vars */
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { StyleSheet, css } from 'aphrodite'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import UITitle from '../../../components/UI/UITitle'
import UISubtitle from '../../../components/UI/UISubtitle'
import UIInput from '../../../components/UI/UIInput'
import UIRadio from '../../../components/UI/UIRadio'
import LayoutRow from '../../../components/Layout/LayoutRow'
import LayoutCol from '../../../components/Layout/LayoutCol'
import UIDatePicker from './../../../components/UI/UIDatePicker'
import { SPACE } from '../../../lib/spaces'
import UIButton from './../../../components/UI/UIButton'
import UIInputError from './../../../components/UI/UIInputError'
import UISelfEmployed from './../../../components/UI/UISelfEmployed'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  emailRegExp,
  phoneRegExp,
  passwordRegExp,
  siretRegExp,
} from '../../../lib/helpers'
import { Auth, Amplify } from 'aws-amplify'
import config from '../../../config/aws'
import { BP_XS, MEDIA_MIN_SM } from '../../../lib/breakpoints'
import { COLOR_BLACK, COLOR_GREEN__100 } from '../../../lib/colors'
import { WIDTH } from '../../../lib/helpers'
import UIPassword from '../../../components/UI/UIPassword'
import parsePhoneNumber from 'libphonenumber-js'
import { toast } from 'react-toastify'
import INT from '../../../config/index'
import UIAutocompleteCity from '../../../components/UI/UIAutocompleteCity'

Amplify.configure({
  Auth: config.aws,
})

const styles = StyleSheet.create({
  link: {
    fontSize: '20px',
    fontFamily: 'Helvetica',
    color: COLOR_BLACK,
    textDecoration: 'underline',
    fontWeight: 'bold',
    margin: '0 5px',
  },
  container: {
    margin: 0,
    paddingBottom: SPACE * 10,
  },
  containerForm: {
    display: 'flex',
    flexDirection: 'Column',
    width: '100%',
  },
  message: {
    fontWeight: 400,
    fontSize: 14,
  },
  info: {
    fontWeight: 400,
    fontSize: 10,
    margin: '10px 0',
    [MEDIA_MIN_SM]: {
      margin: 0,
    },
  },
  submitButton: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  isAdultMargin: {
    marginLeft: 0,
    width: '40vw',
    justifyContent: 'space-between',
    marginTop: 10,
    [MEDIA_MIN_SM]: {
      marginLeft: 20,
      justifyContent: 'flex-start',
      width: 'auto',
      marginTop: 0,
    },
  },
  alreadyWorkMargin: {
    marginLeft: 0,
    width: '40vw',
    justifyContent: 'space-between',
    marginTop: 10,
    [MEDIA_MIN_SM]: {
      justifyContent: 'flex-start',
      width: 'auto',
      marginTop: 0,
    },
  },
  space: {
    marginTop: '5vh',
  },
  btnWidth: {
    width: 315,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: COLOR_BLACK,
    [MEDIA_MIN_SM]: {
      fontSize: 48,
    },
  },
  subTitle: {
    backgroundColor: COLOR_GREEN__100,
    fontFamily: 'Helvetica',
    fontWeight: 'bold',
    borderRadius: '8px',
    padding: '15px 40px',
    transform: 'translateX(-40px)',
    [MEDIA_MIN_SM]: {
      width: '40vw',
    },
    marginBottom: '10px',
  },
  subTitle2: {
    backgroundColor: COLOR_GREEN__100,
    fontFamily: 'Helvetica',
    fontSize: '20px',
    fontWeight: 'bold',
    borderRadius: '8px',
    padding: '15px 40px',
    transform: 'translateX(-40px)',
    [MEDIA_MIN_SM]: {
      width: '40vw',
    },
    marginBottom: '10px',
  },
  row: {
    flexDirection: 'column',
  },
  input: {
    width: '100%',
  },
})

export const AdministrativeQuestions = () => {
  const { t } = useTranslation('translation')
  let navigate = useNavigate()
  const [citySelected, setCitySelected] = useState('')
  const [cityEmptyError, setCityEmptyError] = useState(false)

  const [birthCitySelected, setBirthCitySelected] = useState(
    '7fbd86aa-d8c4-4631-8d07-520c7d08d4e6'
  )
  const [birthCityEmptyError, setBirthCityEmptyError] = useState(false)

  const schema = yup.object().shape({
    lastName: yup
      .string()
      .required(t('common.form.errorMessage.lastNameEmptyError')),
    firstName: yup
      .string()
      .required(t('common.form.errorMessage.firstNameEmptyError')),
    email: yup
      .string()
      .matches(
        emailRegExp,
        t('common.form.errorMessage.invalidEmailEmptyError')
      )
      .required(t('common.form.errorMessage.emailEmptyError')),
    phoneNumber: yup
      .string()
      .matches(
        phoneRegExp,
        t('common.form.errorMessage.phoneNumberNotValidError')
      )
      .required(t('common.form.errorMessage.phoneNumberEmptyError')),
    password: yup
      .string()
      .matches(
        passwordRegExp,
        t('common.form.errorMessage.passwordNotValidError')
      )
      .required(t('common.form.errorMessage.passwordEmptyError')),
    passwordControl: yup
      .string()
      .oneOf([yup.ref('password'), null], t('common.form.errorMessage.noMatch'))
      .required(t('common.form.errorMessage.passwordEmptyError')),
    birthday: yup
      .date()
      .required(t('common.form.errorMessage.birthdayEmptyError')),
    // birthCountry: yup
    //   .string()
    //   .required(t('common.form.errorMessage.birthCountryEmptyError')),
    // birthDepartement: yup
    //   .string()
    //   .required(t('common.form.errorMessage.birthDepartementEmptyError')),
    // birthCity: yup
    //   .string()
    //   .required(t('common.form.errorMessage.birthCityEmptyError')),
    isAdult: yup
      .boolean()
      .oneOf([true], t('common.form.errorMessage.isMinor'))
      .required(t('common.form.errorMessage.isAdultEmptyError')),
    postalAddress: yup
      .string()
      .required(t('common.form.errorMessage.postalAddressEmptyError')),
    country: yup
      .string()
      .required(t('common.form.errorMessage.countryEmptyError')),
    postalCode: yup
      .number()
      .required(t('common.form.errorMessage.postalCodeEmptyError')),
    // city: yup.string().required(t('common.form.errorMessage.cityEmptyError')),
    rib: yup.string().required(t('common.form.errorMessage.ribEmptyError')),
    bic: yup.string().required(t('common.form.errorMessage.bicEmptyError')),
    siret: yup
      .string()
      .matches(siretRegExp, t('common.form.errorMessage.siretError'))
      .required(t('common.form.errorMessage.siretEmptyError')),
    socialReason: yup
      .string()
      .required(t('common.form.errorMessage.socialReasonEmptyError')),
    civility: yup
      .string()
      .required(t('common.form.errorMessage.isAdultEmptyError')),
  })

  const {
    formState: { errors },
    control,
    handleSubmit,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
  })

  const onCitySelect = (value, selected) => {
    setCitySelected(selected?.node?.id)
    setValue('postalCode', selected?.node?.postcode, { shouldValidate: true })
  }

  const onCityChange = (city) => {
    if (city.length === 0) {
      setValue('postalCode', '')
      setCitySelected('')
      setCityEmptyError(true)
    }
  }

  const onCityBlur = (e) => {
    e.preventDefault()
    citySelected === '' ? setCityEmptyError(true) : setCityEmptyError(false)
  }

  const onBirthCitySelect = (value, selected) => {
    setBirthCitySelected(selected?.node?.id)
  }

  const onBirthCityChange = (city) => {
    if (city.length === 0) {
      setBirthCitySelected('')
      setBirthCityEmptyError(true)
    }
  }

  const onBirthCityBlur = (e) => {
    e.preventDefault()
    birthCitySelected === ''
      ? setBirthCityEmptyError(true)
      : setBirthCityEmptyError(false)
  }

  const onSubmit = (data) => {
    const {
      lastName,
      firstName,
      postalAddress,
      postalCode,
      addressComplement,
      password,
      phoneNumber,
      email,
      siret,
      rib,
      birthday,
      birthCountry,
      birthDepartement,
      birthCity,
      country,
      city,
      civility,
      socialReason,
      bic,
    } = data
    const formatPhone = parsePhoneNumber(phoneNumber, 'FR')
    const user = {
      civility: civility,
      lastname: lastName,
      firstname: firstName,
      phoneNumber: formatPhone.number,
      siret: siret,
      addressLine1: postalAddress,
      addressLine2: addressComplement,
      postcode: postalCode,
      password: password,
      email: email,
      rib: rib,
      birthday: birthday,
      birthCountry: birthCountry,
      birthDepartement: birthDepartement,
      birthCity: birthCity,
      country: country,
      city: city,
      socialReason: socialReason,
      bic: bic,
    }
    signUpMaker(user)
    const json = {}
    json.userMaker = user
    localStorage.setItem('userMaker', JSON.stringify(json))
  }
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [inputValue, setInputValue] = useState(
    `${t('pages.registerMaker.administrativeQuestions.birthday')} ${t(
      'common.form.dd/mm/yyyy'
    )}*`
  )
  const [signUpError, setSignUpError] = useState('')

  const onClose = () => {
    setIsPickerOpen(false)
  }

  const signUpMaker = async (data) => {
    if (citySelected === '') {
      setCityEmptyError(true)
    } else if (birthCitySelected === '') {
      setBirthCityEmptyError(true)
    } else {
      try {
        const { user } = await toast.promise(
          Auth.signUp({
            username: data.email,
            password: data.password,
            attributes: {
              profile: JSON.stringify({
                role: 'ubiqeyes_coeye',
                firstname: data.firstname,
                lastname: data.lastname,
                nickname: '',
                phone_number: data.phoneNumber,
                siret: data.siret,
                iban: data.rib,
                company_name: data.socialReason,
                bic: data.bic,
                birthdate: data.birthday,
                id_city_of_birth: birthCitySelected,
                address_line1: data.addressLine1,
                address_line2: data.addressLine2,
                postcode: data.postcode,
                id_city: citySelected,
              }),
            },
          }),
          {
            pending: t('loading'),
            error: t('error'),
          }
        )
        if (user) {
          navigate('../confirmation')
        }
      } catch (error) {
        setSignUpError(t('common.form.errorMessage.' + error.message))
      }
    }
  }

  return (
    <LayoutRow id="1">
      <LayoutCol col={WIDTH <= BP_XS ? 10 : 8} dynamicStyle={styles.container}>
        <UITitle
          label={t('pages.registerMaker.administrativeQuestions.title')}
          dynamicStyle={styles.title}
        />
        <LayoutRow id="1" justifyStart={true} dynamicStyle={styles.space}>
          <UISubtitle
            label={t('pages.registerMaker.administrativeQuestions.subTitle')}
            dynamicStyle={styles.subTitle}
          />
          <div className={css(styles.subTitle2)}>
            <p>
              {t('pages.registerMaker.administrativeQuestions.tutoLinkTitle')}
              <a
                className={css(styles.link)}
                href="https://www.youtube.com/watch?v=pF2mCRIAXDw"
                target={'_blank'}
                rel="noreferrer"
              >
                {t('pages.registerMaker.administrativeQuestions.tutorial')}
              </a>
            </p>
          </div>
        </LayoutRow>
        <div className={css(styles.space)}>
          <UISelfEmployed
            title={t(
              'pages.registerMaker.administrativeQuestions.noSelfEmployed'
            )}
            subTitle={t(
              'pages.registerMaker.administrativeQuestions.selfEmployedSubTitle'
            )}
            description1={t(
              'pages.registerMaker.administrativeQuestions.selfEmployedDesc1'
            )}
            urssafLink={t(
              'pages.registerMaker.administrativeQuestions.urssafLink'
            )}
            description2={t(
              'pages.registerMaker.administrativeQuestions.selfEmployedDesc2'
            )}
            formGouvLink={t(
              'pages.registerMaker.administrativeQuestions.formGouvLink'
            )}
            description3={t(
              'pages.registerMaker.administrativeQuestions.selfEmployedDesc3'
            )}
            cfeLink={t('pages.registerMaker.administrativeQuestions.cfeLink')}
          ></UISelfEmployed>
        </div>
        <LayoutRow justifyStart={true} dynamicStyle={styles.space}>
          <UISubtitle
            label={t(
              'pages.registerMaker.administrativeQuestions.personnalInformations'
            )}
          />
        </LayoutRow>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className={css(styles.containerForm)}
        >
          <LayoutRow justifyStart={true} dynamicStyle={styles.space}>
            <h5 className={css(styles.message)}>{t('common.form.civility')}</h5>
            <Controller
              name="civility"
              control={control}
              render={({ field }) => (
                <LayoutRow dynamicStyle={styles.isAdultMargin}>
                  <UIRadio
                    {...field}
                    checked={field?.value === t('common.mister')}
                    label={t('common.mister')}
                    value={t('common.mister')}
                  />
                  <UIRadio
                    {...field}
                    checked={field?.value === t('common.miss')}
                    label={t('common.miss')}
                    value={t('common.miss')}
                  />
                </LayoutRow>
              )}
            />
          </LayoutRow>
          <UIInputError
            errorMessage={errors.civility && errors.civility.message}
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                placeholder={`${t('common.form.lastName')}*`}
                errorMessage={errors.lastName && errors.lastName.message}
              />
            )}
          />
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                placeholder={`${t('common.form.firstName')}*`}
                errorMessage={errors.firstName && errors.firstName.message}
              />
            )}
          />
          <Controller
            name="birthday"
            control={control}
            render={({ field }) => (
              <UIDatePicker
                {...field}
                placeholder={`${t(
                  'pages.registerMaker.administrativeQuestions.birthday'
                )} ${t('common.form.dd/mm/yyyy')}*`}
                errorMessage={errors.birthday && errors.birthday.message}
                isPickerOpen={isPickerOpen}
                setIsPickerOpen={setIsPickerOpen}
                inputValue={inputValue}
                setInputValue={setInputValue}
                closeDatePicker={onClose}
                onDateChange={(event) => {
                  field.onChange(event)
                  setInputValue(event)
                }}
              />
            )}
          />
          {/* <Controller
            name="birthCountry"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                placeholder={`${t(
                  'pages.registerMaker.administrativeQuestions.birthCountry'
                )}*`}
                errorMessage={
                  errors.birthCountry && errors.birthCountry.message
                }
              />
            )}
          /> */}
          {/* {WIDTH <= BP_XS ? (
            <>
              <UIAutocompleteCity
                placeholder={t(
                  'pages.registerMaker.administrativeQuestions.birthCity'
                )}
                onSelect={onBirthCitySelect}
                onChange={onBirthCityChange}
                onBlur={onBirthCityBlur}
                dynamicStyle={styles.input}
                errorMessage={
                  birthCityEmptyError &&
                  t('common.form.errorMessage.cityEmptyError')
                }
              />
              <Controller
                name="birthDepartement"
                control={control}
                render={({ field }) => (
                  <UIInput
                    {...field}
                    placeholder={`${t(
                      'pages.registerMaker.administrativeQuestions.birthDepartement'
                    )}*`}
                    errorMessage={
                      errors.birthDepartement && errors.birthDepartement.message
                    }
                  />
                )}
              />
            </>
          ) : (
            <LayoutRow gutter="large">
              <LayoutCol col={6}>
                <Controller
                  name="birthDepartement"
                  control={control}
                  render={({ field }) => (
                    <UIInput
                      {...field}
                      placeholder={`${t(
                        'pages.registerMaker.administrativeQuestions.birthDepartement'
                      )}*`}
                      errorMessage={
                        errors.birthDepartement &&
                        errors.birthDepartement.message
                      }
                    />
                  )}
                />
              </LayoutCol>
              <LayoutCol col={6}>
                <UIAutocompleteCity
                  placeholder={t(
                    'pages.registerMaker.administrativeQuestions.birthCity'
                  )}
                  onSelect={onBirthCitySelect}
                  onChange={onBirthCityChange}
                  onBlur={onBirthCityBlur}
                  dynamicStyle={styles.input}
                  errorMessage={
                    birthCityEmptyError &&
                    t('common.form.errorMessage.cityEmptyError')
                  }
                />
              </LayoutCol>
            </LayoutRow>
          )} */}
          <LayoutRow justifyStart={true} dynamicStyle={styles.space}>
            <UISubtitle
              label={t('pages.registerMaker.administrativeQuestions.password')}
            />
          </LayoutRow>
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <UIPassword
                {...field}
                placeholder={`${t('common.form.password')}*`}
                errorMessage={errors.password && errors.password.message}
              />
            )}
          />
          <Controller
            name="passwordControl"
            control={control}
            render={({ field }) => (
              <UIPassword
                {...field}
                placeholder={`${t('common.form.passwordAgain')}*`}
                errorMessage={
                  errors.passwordControl && errors.passwordControl.message
                }
              />
            )}
          />
          {WIDTH <= BP_XS ? (
            <div className={css(styles.space)}>
              <UISubtitle
                label={t('pages.registerMaker.administrativeQuestions.isAdult')}
              />
              <p className={css(styles.info)}>
                {t(
                  'pages.registerMaker.administrativeQuestions.isAdultInstruction'
                )}
              </p>
              <Controller
                name="isAdult"
                control={control}
                render={({ field }) => (
                  <LayoutRow
                    justifyStart={true}
                    dynamicStyle={styles.isAdultMargin}
                  >
                    <UIRadio
                      {...field}
                      checked={field?.value === 'true'}
                      label={t('common.yes')}
                      value={true}
                    />
                    <UIRadio
                      {...field}
                      checked={field?.value === 'false'}
                      label={t('common.no')}
                      value={false}
                    />
                  </LayoutRow>
                )}
              />
            </div>
          ) : (
            <>
              <LayoutRow justifyStart={true} dynamicStyle={styles.space}>
                <UISubtitle
                  label={t(
                    'pages.registerMaker.administrativeQuestions.isAdult'
                  )}
                />
                <Controller
                  name="isAdult"
                  control={control}
                  render={({ field }) => (
                    <LayoutRow dynamicStyle={styles.isAdultMargin}>
                      <UIRadio
                        {...field}
                        checked={field?.value === 'true'}
                        label={t('common.yes')}
                        value={true}
                      />
                      <UIRadio
                        {...field}
                        checked={field?.value === 'false'}
                        label={t('common.no')}
                        value={false}
                      />
                    </LayoutRow>
                  )}
                />
              </LayoutRow>
              <span className={css(styles.info)}>
                {t(
                  'pages.registerMaker.administrativeQuestions.isAdultInstruction'
                )}
              </span>
              <UIInputError
                errorMessage={errors.isAdult && errors.isAdult.message}
              />
            </>
          )}
          <div className={css(styles.space)}>
            <UISubtitle
              label={t(
                'pages.registerMaker.administrativeQuestions.yourAddress'
              )}
            />
            <h5 className={css(styles.message)}>
              {t(
                'pages.registerMaker.administrativeQuestions.yourAddressInstructions'
              )}
            </h5>
          </div>
          <Controller
            name="postalAddress"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                placeholder={`${t('common.form.postalAddress')}*`}
                errorMessage={
                  errors.postalAddress && errors.postalAddress.message
                }
              />
            )}
          />
          <Controller
            name="addressComplement"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                placeholder={`${t('common.form.addressComplement')}`}
              />
            )}
          />
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                placeholder={`${t('common.form.country')}*`}
                errorMessage={errors.country && errors.country.message}
              />
            )}
          />
          {WIDTH <= BP_XS ? (
            <>
              <UIAutocompleteCity
                placeholder={t('common.form.city')}
                onSelect={onCitySelect}
                onChange={onCityChange}
                onBlur={onCityBlur}
                dynamicStyle={styles.input}
                errorMessage={
                  cityEmptyError && t('common.form.errorMessage.cityEmptyError')
                }
              />
              <Controller
                name="postalCode"
                control={control}
                render={({ field }) => (
                  <UIInput
                    {...field}
                    placeholder={`${t('common.form.postalCode')}*`}
                    errorMessage={
                      errors.postalCode && errors.postalCode.message
                    }
                  />
                )}
              />
            </>
          ) : (
            <LayoutRow gutter="large">
              <LayoutCol col={6}>
                <UIAutocompleteCity
                  placeholder={t('common.form.city')}
                  onSelect={onCitySelect}
                  onChange={onCityChange}
                  onBlur={onCityBlur}
                  dynamicStyle={styles.input}
                  errorMessage={
                    cityEmptyError &&
                    t('common.form.errorMessage.cityEmptyError')
                  }
                />
              </LayoutCol>
              <LayoutCol col={6}>
                <Controller
                  name="postalCode"
                  control={control}
                  render={({ field }) => (
                    <UIInput
                      {...field}
                      placeholder={`${t('common.form.postalCode')}*`}
                      errorMessage={
                        errors.postalCode && errors.postalCode.message
                      }
                    />
                  )}
                />
              </LayoutCol>
            </LayoutRow>
          )}
          <div className={css(styles.space)}>
            <UISubtitle
              label={t('pages.registerMaker.administrativeQuestions.email')}
            />
          </div>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                placeholder={`${t('common.form.email')}*`}
                errorMessage={errors.email && errors.email.message}
              />
            )}
          />
          <div className={css(styles.space)}>
            <UISubtitle
              label={t('pages.registerMaker.administrativeQuestions.phone')}
            />
            <h5 className={css(styles.message)}>
              {t(
                'pages.registerMaker.administrativeQuestions.phoneInstruction'
              )}
            </h5>
          </div>
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                type="tel"
                placeholder={`${t('common.form.phoneNumber')}*`}
                errorMessage={errors.phoneNumber && errors.phoneNumber.message}
              />
            )}
          />

          <div className={css(styles.space)}>
            <UISubtitle
              label={t('pages.registerMaker.administrativeQuestions.society')}
            />
            <h5 className={css(styles.message)}>
              {t(
                'pages.registerMaker.administrativeQuestions.societyInstruction'
              )}
            </h5>
          </div>
          <Controller
            name="socialReason"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                placeholder={`${t('common.form.socialReason')}*`}
                errorMessage={
                  errors.socialReason && errors.socialReason.message
                }
              />
            )}
          />
          <Controller
            name="siret"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                placeholder={`${t('common.form.siret')}*`}
                errorMessage={errors.siret && errors.siret.message}
              />
            )}
          />
          <Controller
            name="rib"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                placeholder={`${t('common.form.rib')}*`}
                errorMessage={errors.rib && errors.rib.message}
              />
            )}
          />
          <Controller
            name="bic"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                placeholder={`${t('common.form.bic')}*`}
                errorMessage={errors.bic && errors.bic.message}
              />
            )}
          />
          <LayoutRow justifyEnd={true} dynamicStyle={styles.space}>
            <div className={css(styles.btnWidth)}>
              <UIButton
                type="submit"
                kind="button"
                withForwardChevron
                fullWidth
                design="primary"
                disabled={INT.AUTH_PERM == false ? true : false}
                label={`${t('common.button.submit')}`}
              />
            </div>
          </LayoutRow>
          {signUpError && <UIInputError errorMessage={t(signUpError)} />}
        </form>
      </LayoutCol>
    </LayoutRow>
  )
}
